/**
 * Unsent Write drafts (PLAN.md 4.29), kept in this tab's memory only: switching pages keeps them,
 * closing or reloading the tab drops them. Nothing goes to localStorage or IndexedDB, so the
 * plaintext of a capsule never outlives the tab. While a draft exists, closing the tab asks first.
 */
import type { Prepared } from "./attachments";

export type Draft = { subject: string; intro: string; text: string; attached: Prepared[] };

const drafts = new Map<string, Draft>();

/** One draft per member and Write entry (the same key App gives the Compose page). */
export function draftKey(memberId: number, recipientIds: number[], replyId: string | null): string {
  return `${memberId}:${recipientIds.join(",")}|${replyId ?? ""}`;
}

/** Whether the member wrote anything: a reply's preset "Re: …" subject alone is not a draft. */
export function hasContent(d: Draft, presetSubject = ""): boolean {
  return d.text.trim() !== "" || d.intro.trim() !== "" || d.attached.length > 0 || (d.subject.trim() !== "" && d.subject !== presetSubject);
}

export function loadDraft(key: string): Draft | undefined {
  return drafts.get(key);
}

export function saveDraft(key: string, d: Draft, presetSubject = ""): void {
  if (hasContent(d, presetSubject)) drafts.set(key, d);
  else drafts.delete(key);
  guard();
}

export function dropDraft(key: string): void {
  drafts.delete(key);
  guard();
}

/** The browser's "Leave site?" prompt, only while some draft would be lost. */
export function onBeforeUnload(e: Pick<BeforeUnloadEvent, "preventDefault">): void {
  if (drafts.size > 0) e.preventDefault();
}

let guarding = false;
function guard(): void {
  if (typeof window === "undefined" || guarding === drafts.size > 0) return;
  guarding = drafts.size > 0;
  if (guarding) window.addEventListener("beforeunload", onBeforeUnload);
  else window.removeEventListener("beforeunload", onBeforeUnload);
}
